import { useState } from 'react';
import { Field, Select } from '@/core/ui';
import type { Block, BlockOf } from '../../domain/types';
import { useBuilder } from './context';
import {
  NumberField,
  Section,
  TemplateField,
  TextField,
  Toggle,
  VariableChecklist,
  VariableSelect,
} from './fields';
import { LessonPicker } from './LessonPicker';
import { ChoiceItemsEditor, ConditionEditor } from './ListEditors';

type FormProps<T extends Block['type']> = { block: BlockOf<T>; onChange: (b: BlockOf<T>) => void };

/** O formulário do bloco selecionado — um por tipo. */
export function BlockForm({ block, onChange }: { block: Block; onChange: (b: Block) => void }) {
  switch (block.type) {
    case 'text':
      return (
        <TemplateField
          label="Mensagem"
          multiline
          rows={6}
          value={block.text}
          onChange={(text) => onChange({ ...block, text })}
          hint="**negrito**, *itálico*, [link](https://…) e quebras de linha."
        />
      );
    case 'image':
      return (
        <Section>
          <TextField
            label="Endereço da imagem"
            type="url"
            maxLength={2000}
            value={block.url}
            placeholder="https://…"
            onChange={(url) => onChange({ ...block, url })}
          />
          <TextField
            label="Descrição (texto alternativo)"
            value={block.alt}
            onChange={(alt) => onChange({ ...block, alt })}
            hint="Lida por leitores de tela e mostrada se a imagem não carregar."
          />
        </Section>
      );
    case 'video':
      return (
        <TextField
          label="Endereço do vídeo"
          type="url"
          maxLength={2000}
          value={block.url}
          placeholder="https://youtube.com/watch?v=…"
          onChange={(url) => onChange({ ...block, url })}
          hint="YouTube ou Vimeo (aparece embutido) ou um arquivo de vídeo https."
        />
      );
    case 'link':
      return (
        <Section>
          <TemplateField
            label="Texto do botão"
            value={block.label}
            maxLength={300}
            onChange={(label) => onChange({ ...block, label })}
          />
          <TextField
            label="Endereço"
            maxLength={2000}
            value={block.url}
            placeholder="https://… ou /cursos"
            onChange={(url) => onChange({ ...block, url })}
            hint="https://, mailto:, tel: ou um caminho do portal (começa com /)."
          />
        </Section>
      );
    case 'input_choice':
      return <ChoiceForm block={block} onChange={onChange} />;
    case 'input_text':
    case 'input_email':
    case 'input_phone':
    case 'input_number':
    case 'input_date':
    case 'input_rating':
      return <InputForm block={block} onChange={onChange} />;
    case 'condition':
      return <ConditionEditor block={block} onChange={onChange} />;
    case 'set_variable':
      return (
        <Section>
          <VariableSelect
            label="Variável"
            value={block.variable}
            onChange={(variable) => onChange({ ...block, variable })}
          />
          <TemplateField
            label="Valor"
            value={block.value}
            onChange={(value) => onChange({ ...block, value })}
            placeholder="ex.: {{nome}} — {{tema}}"
          />
        </Section>
      );
    case 'jump':
      return <JumpForm block={block} onChange={onChange} />;
    case 'wait':
      return (
        <NumberField
          label="Segundos"
          value={block.seconds}
          min={0.5}
          max={10}
          step={0.5}
          onChange={(seconds) => onChange({ ...block, seconds: seconds ?? 1 })}
          hint="De 0,5 a 10 segundos: a Centuria “digita” um pouco mais antes de seguir."
        />
      );
    case 'end':
      return (
        <p className="rounded-2xl bg-surface-2 p-3.5 text-sm text-fg-muted">
          A conversa termina aqui. Blocos depois deste no card nunca rodam.
        </p>
      );
    case 'ai_handoff':
      return (
        <Section>
          <p className="text-sm text-fg-muted">
            Encerra o fluxo e abre a conversa com a IA, levando o que foi respondido.
          </p>
          <TemplateField
            label="Primeira pergunta à IA (em nome da pessoa)"
            multiline
            rows={3}
            value={block.question}
            onChange={(question) => onChange({ ...block, question })}
            placeholder="ex.: Quero indicações de {{produto}} para {{objetivo}}"
          />
          <TemplateField
            label="Instruções extras para a IA"
            multiline
            rows={3}
            value={block.instructions}
            onChange={(instructions) => onChange({ ...block, instructions })}
            hint="Contexto que a pessoa não vê — vai junto do resumo da conversa."
          />
        </Section>
      );
    case 'visit_request':
      return <VisitForm block={block} onChange={onChange} />;
    case 'webhook':
      return (
        <Section>
          <TextField
            label="Endereço (https)"
            type="url"
            maxLength={2000}
            value={block.url}
            placeholder="https://…"
            onChange={(url) => onChange({ ...block, url })}
          />
          <VariableChecklist
            label="Variáveis enviadas (JSON)"
            value={block.variables ?? []}
            onChange={(variables) => onChange({ ...block, variables })}
          />
          <VariableSelect
            label="Guardar a resposta em"
            emptyLabel="Não guardar"
            value={block.result_variable}
            onChange={(result_variable) => onChange({ ...block, result_variable })}
          />
          <p className="text-xs text-fg-subtle">
            POST com as variáveis em JSON, tempo limite de 5 s. Se falhar, segue pela saída “Se falhar” — ou
            pela normal, se ela não estiver ligada. No teste o webhook não é chamado.
          </p>
        </Section>
      );
    case 'open':
      return <OpenForm block={block} onChange={onChange} />;
  }
}

function RetryField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <TemplateField
      label="Mensagem se a resposta não servir (opcional)"
      value={value}
      maxLength={300}
      onChange={onChange}
      placeholder="Vazio: a mensagem padrão"
    />
  );
}

function ChoiceForm({ block, onChange }: FormProps<'input_choice'>) {
  return (
    <div className="space-y-3">
      <Section>
        <VariableSelect
          label="Guardar a resposta em"
          value={block.variable}
          onChange={(variable) => onChange({ ...block, variable })}
        />
        <Toggle
          label="Permitir várias opções"
          checked={block.multiple}
          onChange={(multiple) => onChange({ ...block, multiple })}
          hint="A pessoa marca quantas quiser e confirma."
        />
        {block.multiple && (
          <TemplateField
            label="Texto do botão de confirmar"
            value={block.button_label}
            maxLength={300}
            placeholder="Enviar"
            onChange={(button_label) => onChange({ ...block, button_label })}
          />
        )}
      </Section>
      <ChoiceItemsEditor block={block} onChange={onChange} />
      <RetryField
        value={block.retry_message}
        onChange={(retry_message) => onChange({ ...block, retry_message })}
      />
    </div>
  );
}

type SimpleInput = BlockOf<
  'input_text' | 'input_email' | 'input_phone' | 'input_number' | 'input_date' | 'input_rating'
>;

function InputForm({ block, onChange }: { block: SimpleInput; onChange: (b: SimpleInput) => void }) {
  return (
    <div className="space-y-3">
      <Section>
        <VariableSelect
          label="Guardar a resposta em"
          value={block.variable}
          onChange={(variable) => onChange({ ...block, variable })}
        />
        {'placeholder' in block && (
          <TemplateField
            label="Texto de exemplo no campo"
            value={block.placeholder}
            maxLength={300}
            onChange={(placeholder) => onChange({ ...block, placeholder })}
          />
        )}
        {block.type === 'input_text' && (
          <Toggle
            label="Resposta longa"
            checked={block.long}
            onChange={(long) => onChange({ ...block, long })}
            hint="Caixa de várias linhas (até 2.000 caracteres)."
          />
        )}
        {block.type === 'input_phone' && (
          <p className="text-xs text-fg-subtle">
            Aceita celular ou fixo do Brasil, com DDD — guardado como (11) 98765-4321.
          </p>
        )}
        {block.type === 'input_number' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <NumberField label="Mínimo" value={block.min} onChange={(min) => onChange({ ...block, min })} />
              <NumberField label="Máximo" value={block.max} onChange={(max) => onChange({ ...block, max })} />
            </div>
            <Toggle
              label="Só números inteiros"
              checked={block.integer}
              onChange={(integer) => onChange({ ...block, integer })}
            />
          </>
        )}
        {block.type === 'input_date' && (
          <>
            <Toggle
              label="Aceitar datas passadas"
              checked={block.allow_past}
              onChange={(allow_past) => onChange({ ...block, allow_past })}
            />
            <Toggle
              label="Aceitar datas futuras"
              checked={block.allow_future}
              onChange={(allow_future) => onChange({ ...block, allow_future })}
            />
          </>
        )}
        {block.type === 'input_rating' && (
          <>
            <Field label="Nota máxima">
              {(p) => (
                <Select
                  {...p}
                  value={block.max}
                  onChange={(e) => onChange({ ...block, max: Number(e.target.value) })}
                >
                  {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>
                      1 a {n}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Legenda da menor"
                value={block.low_label}
                placeholder="Nada provável"
                onChange={(low_label) => onChange({ ...block, low_label })}
              />
              <TextField
                label="Legenda da maior"
                value={block.high_label}
                placeholder="Muito provável"
                onChange={(high_label) => onChange({ ...block, high_label })}
              />
            </div>
          </>
        )}
      </Section>
      <RetryField
        value={block.retry_message}
        onChange={(retry_message) => onChange({ ...block, retry_message })}
      />
    </div>
  );
}

function JumpForm({ block, onChange }: FormProps<'jump'>) {
  const { doc } = useBuilder();
  return (
    <Field label="Continuar no card" hint="Pular encerra este card: blocos depois deste não rodam.">
      {(p) => (
        <Select
          {...p}
          value={block.group_id}
          onChange={(e) => onChange({ ...block, group_id: e.target.value })}
        >
          <option value="">Escolha o card…</option>
          {doc.groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.title || 'Card sem título'}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

function VisitForm({ block, onChange }: FormProps<'visit_request'>) {
  return (
    <Section>
      <p className="text-sm text-fg-muted">Cria um pedido de visita para o time de relacionamento.</p>
      <VariableSelect
        label="Variável com o WhatsApp"
        value={block.phone_variable}
        readable
        onChange={(phone_variable) => onChange({ ...block, phone_variable })}
        hint="Normalmente a de um bloco Telefone antes deste."
      />
      <VariableChecklist
        label="Variáveis de disponibilidade"
        value={block.availability_variables ?? []}
        onChange={(availability_variables) => onChange({ ...block, availability_variables })}
        hint="Com dias (mon … sat), períodos (morning, afternoon, evening) ou os dois (mon:morning) — use esses códigos como valor das opções. Sem dias: segunda a sexta; sem períodos: qualquer horário."
      />
      <TemplateField
        label="Assunto"
        value={block.topic}
        maxLength={300}
        onChange={(topic) => onChange({ ...block, topic })}
      />
      <TemplateField
        label="Observações para o time"
        multiline
        rows={3}
        value={block.notes}
        onChange={(notes) => onChange({ ...block, notes })}
      />
      <VariableSelect
        label="Guardar o resultado em"
        emptyLabel="Não guardar"
        value={block.result_variable}
        onChange={(result_variable) => onChange({ ...block, result_variable })}
      />
      <p className="text-xs text-fg-subtle">
        Se o pedido falhar, segue pela saída “Se falhar”. No teste nenhum pedido é criado.
      </p>
    </Section>
  );
}

function OpenForm({ block, onChange }: FormProps<'open'>) {
  const [chosen, setChosen] = useState<'lesson' | 'page'>(block.path && !block.lesson_id ? 'page' : 'lesson');
  const mode = block.lesson_id ? 'lesson' : block.path ? 'page' : chosen;
  const pick = (next: 'lesson' | 'page') => {
    setChosen(next);
    onChange(next === 'page' ? { ...block, lesson_id: '', lesson_title: '' } : { ...block, path: '' });
  };
  return (
    <Section>
      <TemplateField
        label="Texto do botão"
        value={block.label}
        maxLength={300}
        onChange={(label) => onChange({ ...block, label })}
      />
      <div role="radiogroup" aria-label="Destino" className="flex gap-1 rounded-xl bg-surface p-1">
        {(
          [
            ['lesson', 'Uma aula'],
            ['page', 'Página do portal'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            onClick={() => pick(value)}
            className={
              mode === value
                ? 'flex-1 rounded-lg bg-nav-active px-3 py-1.5 text-sm font-medium text-nav-active-fg'
                : 'flex-1 rounded-lg px-3 py-1.5 text-sm text-fg-subtle hover:text-fg'
            }
          >
            {label}
          </button>
        ))}
      </div>
      {mode === 'lesson' ? (
        <LessonPicker
          lessonId={block.lesson_id}
          lessonTitle={block.lesson_title}
          onPick={(l) => onChange({ ...block, lesson_id: l.lesson_id, lesson_title: l.title, path: '' })}
          onClear={() => onChange({ ...block, lesson_id: '', lesson_title: '' })}
        />
      ) : (
        <TextField
          label="Caminho no portal"
          value={block.path}
          maxLength={2000}
          placeholder="/cursos"
          onChange={(path) => onChange({ ...block, path })}
          hint="Começa com / — ex.: /cursos, /eventos."
        />
      )}
    </Section>
  );
}
